import React, { useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEvents } from "../../services/eventService";

interface Event {
  id: string;
  title: string;
  date: string;
  description?: string;
  location?: string;
  icon?: React.ReactNode;
  category?: string;
}

interface UpcomingEventsProps {
  title?: string;
  events?: Event[];
  showViewAll?: boolean;
  onViewAll?: () => void;
  onEventClick?: (event: Event) => void;
}

const UpcomingEvents: React.FC<UpcomingEventsProps> = ({
  title,
  events,
  showViewAll = true,
  onViewAll,
  onEventClick,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Fetch upcoming events from API
  const {
    data: eventsResponse,
    isLoading,
    error,
  } = useEvents({
    status: "upcoming",
    perPage: 3,
  });

  // Function to get icon based on category
  function getCategoryIcon(category?: string) {
    const categoryLower = category?.toLowerCase() || "";

    if (
      categoryLower.includes("community") ||
      categoryLower.includes("clean")
    ) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          fill="currentColor"
          viewBox="0 0 256 256"
          className="text-green-600 dark:text-green-400"
        >
          <path d="M208,32H184V24a8,8,0,0,0-16,0v8H88V24a8,8,0,0,0-16,0v8H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM72,48v8a8,8,0,0,0,16,0V48h80v8a8,8,0,0,0,16,0V48h24V80H48V48ZM208,208H48V96H208V208Zm-96-88v64a8,8,0,0,1-16,0V132.94l-4.42,2.22a8,8,0,0,1-7.16-14.32l16-8A8,8,0,0,1,112,120Zm59.16,30.45L152,176h16a8,8,0,0,1,0,16H136a8,8,0,0,1-6.4-12.8l28.78-38.37A8,8,0,1,0,145.07,132a8,8,0,1,1-13.85-8A24,24,0,0,1,176,136,23.76,23.76,0,0,1,171.16,150.45Z" />
        </svg>
      );
    } else if (
      categoryLower.includes("meeting") ||
      categoryLower.includes("general")
    ) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          fill="currentColor"
          viewBox="0 0 256 256"
          className="text-blue-600 dark:text-blue-400"
        >
          <path d="M117.25,157.92a60,60,0,1,0-66.5,0A95.83,95.83,0,0,0,3.53,195.63a8,8,0,0,0,13.4,8.74,80,80,0,0,1,134.14,0,8,8,0,0,0,13.4-8.74A95.83,95.83,0,0,0,117.25,157.92ZM40,108a44,44,0,1,1,44,44A44.05,44.05,0,0,1,40,108Zm210.14,98.7a8,8,0,0,1-11.07-2.33A79.83,79.83,0,0,0,172,168a8,8,0,0,1,0-16,44,44,0,1,0-16.34-84.87,8,8,0,1,1-5.94-14.85,60,60,0,0,1,55.53,105.64,95.83,95.83,0,0,1,47.22,37.71A8,8,0,0,1,250.14,206.7Z" />
        </svg>
      );
    } else if (
      categoryLower.includes("education") ||
      categoryLower.includes("workshop") ||
      categoryLower.includes("leadership")
    ) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          fill="currentColor"
          viewBox="0 0 256 256"
          className="text-purple-600 dark:text-purple-400"
        >
          <path d="M248,128a56,56,0,0,1-95.6,39.4L168.84,166A47.93,47.93,0,0,1,176,128a48,48,0,0,1,48-48,8,8,0,0,1,0,16,32,32,0,0,0-32,32,31.63,31.63,0,0,0,1.6,10l-16.36,16.36A56,56,0,0,1,248,128ZM128,88a40,40,0,1,0-40-40A40,40,0,0,0,128,88Zm0-64a24,24,0,1,1-24,24A24,24,0,0,1,128,24Zm23.6,70.4a8,8,0,0,0-11.2,1.6L128,116.69,115.6,106.4a8,8,0,0,0-12.8,9.6L120.17,132H104a8,8,0,0,0,0,16h24a8,8,0,0,0,6.4-12.8l16.37-16.36A8,8,0,0,0,151.6,94.4ZM80,128a56,56,0,0,1,95.6-39.4L151.16,90A47.93,47.93,0,0,1,144,128a48,48,0,0,1-48,48,8,8,0,0,1,0-16,32,32,0,0,0,32-32,31.63,31.63,0,0,0-1.6-10l16.36-16.36A56,56,0,0,1,80,128Z" />
        </svg>
      );
    } else {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          fill="currentColor"
          viewBox="0 0 256 256"
          className="text-gray-600 dark:text-gray-400"
        >
          <path d="M208,32H184V24a8,8,0,0,0-16,0v8H88V24a8,8,0,0,0-16,0v8H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM72,48v8a8,8,0,0,0,16,0V48h80v8a8,8,0,0,0,16,0V48h24V80H48V48ZM208,208H48V96H208V208Z" />
        </svg>
      );
    }
  }

  // Transform API events to component format
  const apiEvents = useMemo(() => {
    if (!eventsResponse?.data?.events) return [];

    return eventsResponse.data.events.map((event) => ({
      id: event.id,
      title: event.title,
      date: event.date,
      description: event.description,
      location: event.location,
      category: event.category,
      icon: getCategoryIcon(event.category),
    }));
  }, [eventsResponse]);

  const displayEvents = events || apiEvents;

  useEffect(() => {
    if (!isLoading && displayEvents.length > 0) {
      setIsDataLoaded(true);
    } else if (!isLoading) {
      setIsDataLoaded(true);
    }
  }, [isLoading, displayEvents.length]);

  // Loading state
  if (isLoading) {
    return (
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50/50 via-pink-50/30 to-orange-50/50 dark:from-gray-800 dark:via-purple-900/10 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
              {title || t("events.title", "Upcoming Events")}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {t(
                "events.subtitle",
                "Join us for exciting community events and activities"
              )}
            </p>
          </div>
          <div className="max-w-4xl mx-auto flex justify-center items-center py-16">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
              <p className="text-gray-600 dark:text-gray-400">
                {t("events.loading", "Loading events...")}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-stone-50/50 via-emerald-50/30 to-stone-50/50 dark:from-gray-800 dark:via-stone-900/10 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
              {title || t("events.title", "Upcoming Events")}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {t(
                "events.subtitle",
                "Join us for exciting community events and activities"
              )}
            </p>
          </div>
          <div className="max-w-4xl mx-auto flex justify-center items-center py-16">
            <div className="text-center">
              <div className="text-red-500 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t("events.error.title", "Failed to load events")}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t("events.error.message", "Please try again later.")}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
              >
                {t("events.error.retry", "Retry")}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-stone-50/50 via-emerald-50/30 to-stone-50/50 dark:from-gray-800 dark:via-stone-900/10 dark:to-gray-900">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={
              isDataLoaded
                ? { scale: 1, opacity: 1 }
                : { scale: 0.9, opacity: 0 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-stone-800 dark:text-stone-300 rounded-full text-sm font-semibold border border-stone-200 dark:border-stone-800 shadow-lg">
              <span className="text-lg">🎉</span>
              <span>{t("events.badge", "Community Events")}</span>
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={
              isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight"
          >
            {title || t("events.title", "Upcoming Events")}
            <span className="block mt-2 bg-gradient-to-r from-stone-600 via-emerald-600 to-stone-600 bg-clip-text text-transparent">
              {t("events.titleAccent", "Join the Celebration")}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={
              isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto"
          >
            {t(
              "events.subtitle",
              "Join us for exciting community events and activities"
            )}
          </motion.p>
        </motion.div>

        {/* Events Timeline */}
        <div className="max-w-5xl mx-auto">
          <div className="space-y-8">
            {displayEvents.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, x: -50 }}
                animate={
                  isDataLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }
                }
                transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
                whileHover={{ x: 12, scale: 1.02 }}
                className="group relative"
              >
                {/* Floating gradient glow */}
                <div className="absolute -inset-1 bg-gradient-to-r from-stone-500 via-emerald-500 to-stone-500 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>

                {/* Event Card */}
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 overflow-hidden">
                  <div className="flex flex-col lg:flex-row items-start p-6 lg:p-8 gap-6 lg:gap-8">
                    {/* Icon Section */}
                    <motion.div
                      whileHover={{ rotate: 10, scale: 1.15 }}
                      className="flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-stone-100 to-emerald-100 dark:from-stone-900/30 dark:to-emerald-900/30 rounded-2xl flex items-center justify-center self-center lg:self-start shadow-lg group-hover:shadow-xl transition-all duration-300"
                    >
                      {event.icon}
                    </motion.div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 w-full">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl lg:text-2xl font-extrabold text-gray-900 dark:text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-stone-600 group-hover:to-emerald-600 transition-all duration-300">
                            {event.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-300 mb-4">
                            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-lg">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="font-semibold">
                                {event.date}
                              </span>
                            </div>
                            {event.location && (
                              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-lg">
                                <svg
                                  className="w-4 h-4"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span className="font-semibold">
                                  {event.location}
                                </span>
                              </div>
                            )}
                          </div>
                          {event.description && (
                            <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed text-base">
                              {event.description}
                            </p>
                          )}
                        </div>

                        {/* Category Badge */}
                        {event.category && (
                          <span className="inline-flex items-center px-4 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-stone-500 to-emerald-500 text-white shadow-lg whitespace-nowrap self-start lg:self-start">
                            {event.category}
                          </span>
                        )}
                      </div>

                      {/* Action Button */}
                      <motion.button
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          if (onEventClick) {
                            onEventClick(event);
                          } else {
                            navigate(`/events/${event.id}`);
                          }
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-stone-500 to-emerald-600 hover:from-stone-600 hover:to-emerald-700 text-white rounded-xl font-bold transition-all duration-200 text-sm shadow-lg hover:shadow-xl"
                      >
                        {t("events.learnMore", "Learn More")}
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </motion.button>
                    </div>
                  </div>

                  {/* Decorative gradient line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-stone-500 via-emerald-500 to-stone-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* View All Button */}
        {showViewAll && displayEvents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={
              isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
            }
            transition={{ duration: 0.6, delay: 1 }}
            className="text-center mt-20"
          >
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (onViewAll) {
                  onViewAll();
                } else {
                  navigate("/events");
                }
              }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 text-gray-900 dark:text-white rounded-2xl font-bold text-lg transition-all duration-200 shadow-xl hover:shadow-2xl border-2 border-gray-200 dark:border-gray-700"
            >
              {t("events.viewAll", "View All Events")}
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default UpcomingEvents;
